// FILE: frontend/src/components/CreatePost.js  (NEW FILE)
import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Film, Image, X, Search, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function CreatePost({ currentUser, onPostCreated }) {
  const [content, setContent]             = useState('');
  const [imageUrl, setImageUrl]           = useState('');
  const [imagePreview, setImagePreview]   = useState('');
  const [taggedMovie, setTaggedMovie]     = useState(null);
  const [showMovieSearch, setShowMovieSearch] = useState(false);
  const [movieQuery, setMovieQuery]       = useState('');
  const [movieResults, setMovieResults]   = useState([]);
  const [searching, setSearching]         = useState(false);
  const [submitting, setSubmitting]       = useState(false);
  const [focused, setFocused]             = useState(false);
  const searchTimeout                     = useRef(null);

  if (!currentUser) return null;

  const handleImageUrl = (url) => {
    setImageUrl(url);
    setImagePreview(url);
  };

  const handleMovieSearch = (q) => {
    setMovieQuery(q);
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (!q.trim()) { setMovieResults([]); return; }
    searchTimeout.current = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await axios.get(`${BACKEND_URL}/api/search?query=${encodeURIComponent(q)}&type=multi`);
        setMovieResults((res.data.results || []).slice(0, 6));
      } catch { /* ignore */ }
      finally { setSearching(false); }
    }, 300);
  };

  const selectMovie = (movie) => {
    setTaggedMovie({
      id:          movie.id,
      title:       movie.title || movie.name,
      poster_path: movie.poster_path,
      media_type:  movie.media_type || (movie.first_air_date ? 'tv' : 'movie'),
    });
    setShowMovieSearch(false);
    setMovieQuery('');
    setMovieResults([]);
  };

  const handleSubmit = async () => {
    if (!content.trim()) { toast.error('Write something first'); return; }
    if (content.length > 2000) { toast.error('Too long — max 2000 characters'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(
        `${BACKEND_URL}/api/posts`,
        {
          content,
          image_url:           imageUrl || null,
          tagged_movie_id:     taggedMovie?.id || null,
          tagged_movie_title:  taggedMovie?.title || null,
          tagged_movie_poster: taggedMovie?.poster_path || null,
          tagged_media_type:   taggedMovie?.media_type || null,
        },
        { withCredentials: true }
      );
      onPostCreated?.(res.data);
      setContent('');
      setImageUrl('');
      setImagePreview('');
      setTaggedMovie(null);
      setFocused(false);
      toast.success('Posted!');
    } catch (err) {
      if (err.response?.status === 401) toast.error('Please login first');
      else toast.error('Failed to post');
    } finally {
      setSubmitting(false);
    }
  };

  const charCount  = content.length;
  const charLimit  = 2000;
  const nearLimit  = charCount > 1800;
  const overLimit  = charCount > charLimit;

  return (
    <div className={`bg-card border rounded-2xl overflow-hidden transition-all duration-200 ${
      focused ? 'border-primary/40 shadow-lg shadow-primary/5' : 'border-white/6'
    }`}>
      <div className="p-4">
        <div className="flex gap-3">
          <img
            src={currentUser.picture}
            alt={currentUser.name}
            className="w-10 h-10 rounded-full border border-white/10 flex-shrink-0 mt-0.5"
          />
          <div className="flex-1 min-w-0">
            <textarea
              value={content}
              onChange={e => setContent(e.target.value)}
              onFocus={() => setFocused(true)}
              placeholder="What are you watching? Share a thought…"
              className="w-full bg-transparent text-white placeholder-gray-500 text-[15px] leading-relaxed resize-none focus:outline-none min-h-[52px]"
              style={{ height: focused ? '120px' : '52px', transition: 'height 0.2s ease' }}
            />

            {/* Image preview */}
            {imagePreview && (
              <div className="relative mt-2 rounded-xl overflow-hidden border border-white/10 inline-block">
                <img src={imagePreview} alt="preview"
                  className="max-h-48 max-w-full object-cover rounded-xl"
                  onError={() => { setImagePreview(''); setImageUrl(''); }}
                />
                <button
                  onClick={() => { setImageUrl(''); setImagePreview(''); }}
                  className="absolute top-2 right-2 bg-black/60 text-white rounded-full p-1 hover:bg-black/80 transition-colors"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            )}

            {/* Movie tag */}
            {taggedMovie && (
              <div className="flex items-center gap-3 mt-2 bg-white/4 border border-white/8 rounded-xl p-2.5 pr-3">
                {taggedMovie.poster_path && (
                  <img
                    src={`https://image.tmdb.org/t/p/w92${taggedMovie.poster_path}`}
                    alt={taggedMovie.title}
                    className="w-8 h-12 rounded object-cover border border-white/10 flex-shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-[10px] text-primary font-bold uppercase tracking-wider">
                    {taggedMovie.media_type === 'tv' ? 'TV Show' : 'Movie'}
                  </p>
                  <p className="text-sm font-semibold text-white line-clamp-1">{taggedMovie.title}</p>
                </div>
                <button
                  onClick={() => setTaggedMovie(null)}
                  className="text-muted-foreground hover:text-white transition-colors flex-shrink-0"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            )}

            {/* Movie search panel */}
            {showMovieSearch && (
              <div className="mt-2 bg-background/80 border border-white/10 rounded-xl overflow-hidden backdrop-blur-sm">
                <div className="flex items-center gap-2 px-3 py-2 border-b border-white/8">
                  {searching
                    ? <Loader2 className="w-4 h-4 text-primary animate-spin flex-shrink-0" />
                    : <Search className="w-4 h-4 text-muted-foreground flex-shrink-0" />
                  }
                  <input
                    autoFocus
                    value={movieQuery}
                    onChange={e => handleMovieSearch(e.target.value)}
                    placeholder="Search movies & shows…"
                    className="flex-1 bg-transparent text-sm text-white placeholder-gray-500 focus:outline-none"
                  />
                  <button
                    onClick={() => { setShowMovieSearch(false); setMovieQuery(''); setMovieResults([]); }}
                    className="text-muted-foreground hover:text-white transition-colors"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>

                {movieResults.length > 0 && (
                  <div className="max-h-64 overflow-y-auto">
                    {movieResults.map(movie => (
                      <button
                        key={movie.id}
                        onClick={() => selectMovie(movie)}
                        className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-white/5 transition-colors text-left"
                      >
                        {movie.poster_path ? (
                          <img
                            src={`https://image.tmdb.org/t/p/w92${movie.poster_path}`}
                            alt={movie.title || movie.name}
                            className="w-8 h-12 rounded object-cover border border-white/10 flex-shrink-0"
                          />
                        ) : (
                          <div className="w-8 h-12 rounded bg-muted flex items-center justify-center flex-shrink-0">
                            <Film className="w-3.5 h-3.5 text-muted-foreground" />
                          </div>
                        )}
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-white line-clamp-1">
                            {movie.title || movie.name}
                          </p>
                          <p className="text-xs text-muted-foreground capitalize">
                            {movie.media_type === 'tv' ? 'TV Show' : 'Movie'}
                            {(movie.release_date || movie.first_air_date) && (
                              <> · {(movie.release_date || movie.first_air_date).substring(0, 4)}</>
                            )}
                          </p>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {movieQuery && !searching && movieResults.length === 0 && (
                  <p className="text-sm text-muted-foreground px-3 py-3">No results found.</p>
                )}
              </div>
            )}

            {/* Image URL input (visible only when expanded) */}
            {focused && !imagePreview && (
              <div className="mt-2">
                <input
                  value={imageUrl}
                  onChange={e => handleImageUrl(e.target.value)}
                  placeholder="Paste image URL (optional)…"
                  className="w-full bg-white/4 border border-white/8 rounded-xl px-3 py-2 text-sm text-white placeholder-gray-600 focus:outline-none focus:border-white/20 transition-colors"
                />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── FOOTER ───────────────────────────────────────── */}
      {focused && (
        <div className="flex items-center justify-between px-4 pb-4 pt-0 gap-3">
          <div className="flex items-center gap-1">
            {/* Tag movie */}
            <button
              onClick={() => setShowMovieSearch(prev => !prev)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                taggedMovie || showMovieSearch
                  ? 'bg-primary/20 text-primary border border-primary/30'
                  : 'text-muted-foreground hover:text-primary hover:bg-primary/10 border border-transparent'
              }`}
            >
              <Film className="w-3.5 h-3.5" />
              {taggedMovie ? 'Tagged' : 'Tag Movie'}
            </button>

            {/* Image placeholder (for future file upload) */}
            <button
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-muted-foreground hover:text-blue-400 hover:bg-blue-500/10 border border-transparent transition-all"
              title="Paste an image URL in the field above"
            >
              <Image className="w-3.5 h-3.5" />
              Image
            </button>
          </div>

          <div className="flex items-center gap-3">
            {/* Character counter */}
            {charCount > 1600 && (
              <span className={`text-xs font-mono tabular-nums ${
                overLimit ? 'text-red-400' : nearLimit ? 'text-yellow-400' : 'text-muted-foreground'
              }`}>
                {charLimit - charCount}
              </span>
            )}

            <button
              onClick={() => { setFocused(false); setContent(''); setImageUrl(''); setImagePreview(''); setTaggedMovie(null); }}
              className="px-3 py-1.5 rounded-lg text-sm font-semibold text-muted-foreground hover:text-white transition-colors"
            >
              Cancel
            </button>

            <button
              onClick={handleSubmit}
              disabled={submitting || !content.trim() || overLimit}
              className="px-5 py-1.5 bg-primary text-primary-foreground rounded-lg text-sm font-bold hover:bg-primary/90 transition-all active:scale-95 disabled:opacity-40 shadow-md shadow-primary/20"
            >
              {submitting ? 'Posting…' : 'Post'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
